import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  effect,
  EnvironmentProviders,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@shared-app-config';
import {
  buildLandingSeoTags,
  LANDING_SEO_META_SELECTORS,
  LANDING_SITE_NAME,
  LandingSeoPage,
  landingSeoKeys,
} from './landing-seo';

/**
 * Writes the page's title, description, canonical URL and link-preview tags into `<head>` — while
 * the landing is prerendered too, so they are in the static HTML crawlers read. Leaving the page
 * takes them back. Call it in an injection context of the page component.
 */
export function injectLandingSeo(page: LandingSeoPage): void {
  const title = inject(Title);
  const meta = inject(Meta);
  const document = inject(DOCUMENT);
  const origin = environment.frontendUrl.login;
  const keys = landingSeoKeys(page);
  const translations = toSignal<Record<string, unknown>, Record<string, unknown>>(
    inject(TranslateService).stream([keys.title, keys.description]),
    { initialValue: {} },
  );

  effect(() => {
    const texts = translations();
    const tags = buildLandingSeoTags(
      page,
      { title: translated(texts, keys.title), description: translated(texts, keys.description) },
      origin,
    );

    title.setTitle(tags.title);
    LANDING_SEO_META_SELECTORS.forEach((selector) => meta.removeTag(selector));
    const canonical = upsertHeadElement(document, 'link[rel="canonical"]', () => {
      const link = document.createElement('link');
      link.rel = 'canonical';
      return link;
    });
    canonical.setAttribute('href', tags.canonicalUrl);
    placeAfterTitle(document, [canonical, ...meta.addTags(tags.meta)]);
  });

  inject(DestroyRef).onDestroy(() => {
    title.setTitle(LANDING_SITE_NAME);
    LANDING_SEO_META_SELECTORS.forEach((selector) => meta.removeTag(selector));
    document.head.querySelector('link[rel="canonical"]')?.remove();
  });
}

/** Keeps every non-production build — the dev site — out of search results. */
export function provideLandingIndexingPolicy(): EnvironmentProviders {
  return provideEnvironmentInitializer(() => {
    if (!environment.production) {
      const robots = inject(Meta).updateTag({ name: 'robots', content: 'noindex, nofollow' });
      if (robots) placeAfterTitle(inject(DOCUMENT), [robots]);
    }
  });
}

/** An untranslated key comes back as the key itself; for SEO that is "no text yet". */
function translated(texts: Record<string, unknown>, key: string): string {
  const value = texts[key];
  return typeof value === 'string' && value !== key ? value : '';
}

/**
 * `Meta` appends to the end of `<head>`, behind the ~1500 lines of component CSS the prerender
 * inlines there: link-preview scrapers that read only the start of a page would miss the tags, and
 * so does a reader of View Source. Keep them right under the `<title>`.
 */
function placeAfterTitle(document: Document, elements: HTMLElement[]): void {
  const titleElement = document.head.querySelector('title');
  if (!titleElement) return;

  // Backwards, each right under the title: the list keeps its order even when some of its elements
  // already sit there from a previous run.
  [...elements]
    .reverse()
    .forEach((element) => document.head.insertBefore(element, titleElement.nextSibling));
}

/** Reuses the element the prerendered HTML already carries, so hydration doesn't duplicate it. */
function upsertHeadElement(
  document: Document,
  selector: string,
  create: () => HTMLElement,
): HTMLElement {
  const existing = document.head.querySelector<HTMLElement>(selector);
  if (existing) return existing;

  const element = create();
  document.head.appendChild(element);
  return element;
}
